import React from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import {
  X, Check, Loader2, Copy, Trash2, Layers, ArrowRight, CheckSquare,
  Eye, Mail, MessageCircle, Send, CheckCircle2, Trophy, Brain, Folder, Info, RefreshCw,
} from "lucide-react";
import { toast } from "../ui/sonner";
import CustomFieldsForm from "../CustomFieldsForm";
import EmpresaFormDialog from "../EmpresaFormDialog";
import ContatoFormDialog from "../ContatoFormDialog";
import ProdutoFormDialog from "../ProdutoFormDialog";
import EmailComposerModal from "../EmailComposerModal";
import NoteEditorDialog from "./NoteEditorDialog";
import { fmtDate } from "./NegocioHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dynamic JSON renderer — turns any AI response JSON into visual UI
export function JsonRenderer({ data }) {
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data).filter(([k]) => k !== "approved");
  if (entries.length === 0) return null;

  const renderValue = (key, val) => {
    // Boolean → check/x icon
    if (typeof val === "boolean") {
      return (
        <div key={key} className="flex items-center gap-2">
          {val ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <X className="w-3.5 h-3.5 text-rose-500" />}
          <span className="text-xs">{formatKey(key)}</span>
        </div>
      );
    }
    // Number → badge
    if (typeof val === "number") {
      return (
        <div key={key} className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">{formatKey(key)}:</span>
          <span className="text-xs font-bold bg-white/60 px-1.5 py-0.5 rounded">{val}</span>
        </div>
      );
    }
    // Array of objects → checklist
    if (Array.isArray(val)) {
      return (
        <div key={key} className="space-y-1">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">{formatKey(key)}</span>
          {val.map((item, j) => {
            if (typeof item === "object" && item !== null) {
              const label = item.item || item.name || item.label || item.descricao || item.documento || Object.values(item).find(v => typeof v === "string") || JSON.stringify(item);
              const ok = item.ok ?? item.valid ?? item.approved ?? item.status === "ok";
              const hasBoolean = "ok" in item || "valid" in item || "approved" in item || "status" in item;
              return (
                <div key={j} className="flex items-center gap-2 pl-1">
                  {hasBoolean ? (ok ? <Check className="w-3 h-3 text-emerald-500" /> : <X className="w-3 h-3 text-rose-500" />) : <span className="w-3 h-3 text-slate-300">•</span>}
                  <span className="text-xs">{typeof label === "string" ? label : JSON.stringify(label)}</span>
                </div>
              );
            }
            return <div key={j} className="text-xs pl-3">• {String(item)}</div>;
          })}
        </div>
      );
    }
    // String → text
    if (typeof val === "string" && val) {
      return (
        <div key={key}>
          <span className="text-[10px] text-slate-500">{formatKey(key)}:</span>
          <p className="text-xs mt-0.5">{val}</p>
        </div>
      );
    }
    // Nested object
    if (typeof val === "object" && val !== null) {
      return (
        <div key={key} className="space-y-1 pl-2 border-l-2 border-slate-200">
          <span className="text-[10px] font-semibold text-slate-500 uppercase">{formatKey(key)}</span>
          {Object.entries(val).map(([k2, v2]) => renderValue(k2, v2))}
        </div>
      );
    }
    return null;
  };

  const formatKey = (k) => k.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, c => c.toUpperCase());

  return <div className="space-y-1.5">{entries.map(([k, v]) => renderValue(k, v))}</div>;
}

export default function NegocioDialogs({ ctx }) {
  const { t } = useTranslation();
  const {
    negocio, stages, pipeline, customFields, stageFieldsMap, contatos, onSaved, tc500,
    contatoRecord, empresaRecord,
    // Duplicate
    duplicateOpen, setDuplicateOpen, handleDuplicate, actionLoading,
    // Delete
    deleteOpen, setDeleteOpen, handleDelete,
    // Reopen
    reabrirOpen, setReopenOpen, handleReabrir, reabrindo,
    // Win
    ganhandoOpen, setGanhandoOpen, handleGanhar, ganhando, setGanhando, ganhoDate, setGanhoDate, ganhoEditing, setGanhoEditing, ganhoConfirmOpen, setGanhoConfirmOpen,
    // Pending Move
    pendingMoveStageId, setPendingMoveStageId, pendingMoveValues, setPendingMoveValues, savePendingMove,
    // Tasks
    taskDialogOpen, setTaskDialogOpen, editingTask, setEditingTask, taskForm, setTaskForm,
    taskTypes, handleCreateTask, translateTaskType,
    // Conclude Task
    concludeTaskTarget, setConcludeTaskTarget, concludeResultado, setConcludeResultado, handleConcludeTask,
    // Task Delete
    taskDeleteTarget, setTaskDeleteTarget, handleDeleteTask,
    // Task Reopen
    reopenTaskTarget, setReopenTaskTarget, fetchTasks,
    // Edit Entity
    editEmpresaOpen, setEditEmpresaOpen,
    editContatoItem, setEditContatoItem,
    // Quick Create
    quickCreate, setQuickCreate, setForm,
    // Notes
    noteEditorOpen, setNoteEditorOpen, editingNote, setEditingNote,
    noteDeleteTarget, setNoteDeleteTarget, fetchNotes,
    // History Detail
    historyDetailEntry, setHistoryDetailEntry,
    // Email
    emailComposerOpen, setEmailComposerOpen, emailDetailItem, setEmailDetailItem, fetchEmails,
    // AI Context
    aiContextOpen, setAiContextOpen, aiContextText, aiContextLoading,
    // Quick WhatsApp
    quickWaOpen, setQuickWaOpen, quickWaText, setQuickWaText, quickWaSending, handleQuickWaSend,
    // Perder (loss)
    perderOpen, setPerderOpen, motivos, selectedMotivo, setSelectedMotivo, handlePerder, perdendo,
    // View all custom fields
    viewAllOpen, setViewAllOpen,
  } = ctx;

  const [filePickerOpen, setFilePickerOpen] = React.useState(false);
  const [filePickerIdx, setFilePickerIdx] = React.useState(null);
  const [filePickerList, setFilePickerList] = React.useState([]);
  const [filePickerLoading, setFilePickerLoading] = React.useState(false);

  const [aiDetailOpen, setAiDetailOpen] = React.useState(false);

  const openFilePicker = async (docIdx) => {
    setFilePickerIdx(docIdx);
    setFilePickerLoading(true);
    setFilePickerOpen(true);
    try {
      const r = await axios.get(`${API}/files/list?negocio_id=${negocio?.id}`);
      setFilePickerList(r.data || []);
    } catch { setFilePickerList([]); }
    setFilePickerLoading(false);
  };

  return (
    <>
      {/* View All Custom Fields */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5" style={{ color: tc500 }} />
              Todos os campos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(customFields || []).map(cf => {
              const val = negocio?.custom_fields?.[cf.field_name];
              return (
                <div key={cf.field_name} className="flex items-center justify-between py-1.5 border-b border-slate-100">
                  <span className="text-xs text-slate-500">{cf.label}</span>
                  <span className="text-xs text-slate-700 break-all">{String(val ?? "—")}</span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicar */}
      <AlertDialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-slate-500" />Duplicar Negócio</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Deseja duplicar o negócio <strong>"{negocio?.titulo}"</strong>? Uma cópia será criada com os mesmos dados na etapa inicial do pipeline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDuplicate} disabled={actionLoading} className="bg-slate-800 hover:bg-slate-900 text-white">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}Duplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Excluir */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-500" />Excluir Negócio</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Tem certeza que deseja excluir <strong>"{negocio?.titulo}"</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={actionLoading} className="bg-rose-600 hover:bg-rose-700 text-white">
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Entity Create */}
      <EmpresaFormDialog open={quickCreate === "empresa"} onClose={() => setQuickCreate(null)}
        onSaved={(newItem) => { setForm(prev => ({ ...prev, empresa_id: newItem.id })); if (onSaved) onSaved(); setQuickCreate(null); }} />
      <ContatoFormDialog open={quickCreate === "contato"} onClose={() => setQuickCreate(null)}
        onSaved={(newItem) => { setForm(prev => ({ ...prev, contato_id: newItem.id })); if (onSaved) onSaved(); setQuickCreate(null); }} />
      <ProdutoFormDialog open={quickCreate === "produto"} onClose={() => setQuickCreate(null)}
        onSaved={(newItem) => { setForm(prev => ({ ...prev, produto_id: newItem.id })); if (onSaved) onSaved(); setQuickCreate(null); }} />

      {/* Reabrir */}
      <AlertDialog open={reabrirOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-slate-500" />Reabrir Negócio</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">{t("negocios.reopenConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X className="w-4 h-4 mr-2" />{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReabrir} disabled={reabrindo} className="bg-slate-800 hover:bg-slate-900 text-white">
              {reabrindo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}Reabrir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ganhar — Simple confirm */}
      <Dialog open={ganhandoOpen} onOpenChange={setGanhandoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700"><Trophy className="w-5 h-5" />{ganhoEditing ? "Editar Data de Ganho" : t("negocios.winTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">{ganhoEditing ? "Altere a data em que este negócio foi ganho." : t("negocios.winConfirm")}</p>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-slate-700">Data do ganho</Label>
              <Input type="date" value={ganhoDate} onChange={e => setGanhoDate(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setGanhandoOpen(false); setGanhoEditing(false); }}>
              <X className="w-4 h-4 mr-2" />{t("common.cancel")}
            </Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={ganhando || !ganhoDate}
              onClick={() => {
                if (ganhoEditing) {
                  // Check if date changed — confirm before saving
                  const existing = negocio.ganho_em || negocio.data_finalizacao || "";
                  const existingDate = existing ? new Date(existing).toISOString().slice(0, 10) : "";
                  if (ganhoDate !== existingDate) {
                    setGanhoConfirmOpen(true);
                  } else {
                    setGanhandoOpen(false);
                    setGanhoEditing(false);
                    toast.info("Data não foi alterada.");
                  }
                } else {
                  (async () => {
                    setGanhando(true);
                    try { await handleGanhar(); } catch { toast.error("Erro ao ganhar"); }
                    setGanhando(false);
                  })();
                }
              }}>
              {ganhando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
              {ganhoEditing ? "Salvar Data" : "Confirmar Ganho"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm date change */}
      <AlertDialog open={ganhoConfirmOpen} onOpenChange={setGanhoConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alterar data de ganho?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-500">
                A data de ganho será alterada de{" "}
                <strong>{(() => { try { return negocio.ganho_em ? new Date(negocio.ganho_em).toLocaleDateString("pt-BR") : "—"; } catch { return "—"; } })()}</strong>
                {" "}para{" "}
                <strong>{(() => { try { return ganhoDate ? new Date(ganhoDate + "T12:00:00").toLocaleDateString("pt-BR") : "—"; } catch { return "—"; } })()}</strong>
                . Isso afetará relatórios e métricas. Deseja continuar?
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={ganhando}
              onClick={async () => {
                setGanhoConfirmOpen(false);
                setGanhando(true);
                try {
                  const payload = { ganho_em: new Date(ganhoDate + "T12:00:00").toISOString() };
                  await axios.patch(`${API}/negocios/${negocio.id}/ganhar`, payload, { withCredentials: true });
                  toast.success("Data de ganho atualizada!");
                  setGanhandoOpen(false);
                  setGanhoEditing(false);
                  if (onSaved) onSaved();
                } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atualizar"); }
                setGanhando(false);
              }}>
              {ganhando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Confirmar Alteração
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File Picker Modal */}
      <Dialog open={filePickerOpen} onOpenChange={setFilePickerOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm"><Folder className="w-4 h-4" style={{ color: tc500 }} />Selecionar arquivo</DialogTitle>
          </DialogHeader>
          {filePickerLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
          ) : filePickerList.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nenhum arquivo neste negócio.</p>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {filePickerList.map(f => {
                const ext = f.original_filename?.split(".").pop()?.toUpperCase() || "?";
                const size = f.size > 1024 * 1024 ? `${(f.size / 1024 / 1024).toFixed(1)} MB` : `${(f.size / 1024).toFixed(0)} KB`;
                return (
                  <button key={f.id} onClick={() => {
                    setFilePickerOpen(false);
                  }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left">
                    <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-slate-500">{ext}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{f.original_filename}</p>
                      <p className="text-[10px] text-slate-400">{size} · {new Date(f.created_at).toLocaleDateString("pt-BR")}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pending Move */}
      <Dialog open={!!pendingMoveStageId} onOpenChange={() => { setPendingMoveStageId(null); setPendingMoveValues({}); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ArrowRight className="w-5 h-5" style={{ color: tc500 }} />{t("negocios.fillRequiredFields")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-1">
            Para mover <strong>{negocio?.titulo}</strong> {t("negocios.moveToStage_pre")} <strong>{stages.find(s => s.id === pendingMoveStageId)?.name}</strong>{t("negocios.moveToStage_post")}
          </p>
          <div className="space-y-3">
            {pendingMoveStageId && (() => {
              const sourceRequired = stageFieldsMap[negocio?.estagio_id] || [];
              const destRequired   = stageFieldsMap[pendingMoveStageId] || [];
              const allRequired    = [...new Set([...sourceRequired, ...destRequired])];
              const missingFields  = allRequired.map(fn => customFields.find(c => c.field_name === fn)).filter(Boolean)
                .filter(cf => { const v = negocio?.custom_fields?.[cf.field_name]; return !v && v !== 0; });
              if (missingFields.length === 0) return <p className="text-sm text-slate-400 text-center py-4">{t("negocios.allFieldsFilled")}</p>;
              return missingFields.map(cf => (
                <CustomFieldsForm key={cf.field_name} fields={[{ ...cf, required: true }]} values={pendingMoveValues} onChange={vals => setPendingMoveValues(vals)} />
              ));
            })()}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setPendingMoveStageId(null); setPendingMoveValues({}); }}>
              <X className="w-4 h-4 mr-2" />{t("common.cancel")}
            </Button>
            <Button className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} onClick={savePendingMove}>
              <Check className="w-4 h-4 mr-2" />{t("negocios.saveAndMove")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nova Tarefa */}
      <Dialog open={taskDialogOpen} onOpenChange={(v) => { setTaskDialogOpen(v); if (!v) setEditingTask(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckSquare className="w-5 h-5" style={{ color: tc500 }} />{editingTask ? t("tarefas.editTitle") : t("tarefas.new")}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-3">
            <div><Label className="text-slate-700 text-sm">{t("negocios.titleField")} <span className="text-rose-500">*</span></Label>
              <Input value={taskForm.titulo} onChange={e => setTaskForm({ ...taskForm, titulo: e.target.value })} placeholder={t("tarefas.taskTitlePlaceholder")} required className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-700 text-sm">{t("tarefas.type")}</Label>
                <Select value={taskForm.tipo} onValueChange={v => setTaskForm({ ...taskForm, tipo: v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder={`${t("tarefas.type")}...`} /></SelectTrigger>
                  <SelectContent>{taskTypes.map(tt => <SelectItem key={tt.id} value={tt.name}>{translateTaskType(tt.name)}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-slate-700 text-sm">{t("tarefas.priority")}</Label>
                <Select value={taskForm.prioridade} onValueChange={v => setTaskForm({ ...taskForm, prioridade: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">{t("tarefas.low")}</SelectItem><SelectItem value="media">{t("tarefas.medium")}</SelectItem>
                    <SelectItem value="alta">{t("tarefas.high")}</SelectItem><SelectItem value="urgente">{t("tarefas.urgent")}</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-slate-700 text-sm">{t("tarefas.dueDate")}</Label>
              <Input type="datetime-local" value={taskForm.data_vencimento} onChange={e => setTaskForm({ ...taskForm, data_vencimento: e.target.value })} className="mt-1" /></div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setTaskDialogOpen(false); setEditingTask(null); }}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
              <Button type="submit" className="flex-1 text-white hover:opacity-90" style={{ backgroundColor: tc500 }}><Check className="w-4 h-4 mr-2" />{editingTask ? t("common.save") || "Salvar" : t("common.create")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Concluir Tarefa */}
      <Dialog open={!!concludeTaskTarget} onOpenChange={() => setConcludeTaskTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" />Concluir Tarefa</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mb-3">Tarefa: <strong>{concludeTaskTarget?.titulo}</strong></p>
          <div><Label className="text-slate-700 text-sm">Resultado (opcional)</Label>
            <Textarea value={concludeResultado} onChange={e => setConcludeResultado(e.target.value)} placeholder="O que foi feito?" rows={2} className="mt-1" /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConcludeTaskTarget(null)}><X className="w-4 h-4 mr-2" />{t("common.cancel")}</Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConcludeTask}><Check className="w-4 h-4 mr-2" />Concluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Delete */}
      <AlertDialog open={!!taskDeleteTarget} onOpenChange={() => setTaskDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tarefas.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("tarefas.deleteConfirm", { titulo: taskDeleteTarget?.titulo || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDeleteTask}>{t("common.delete") || "Excluir"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Reopen */}
      <AlertDialog open={!!reopenTaskTarget} onOpenChange={() => setReopenTaskTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reabrir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>Deseja realmente reabrir a tarefa "{reopenTaskTarget?.titulo}"? Ela voltará para o status "Aberta".</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-amber-600 hover:bg-amber-700 text-white" onClick={async () => {
              if (!reopenTaskTarget) return;
              try {
                await axios.patch(`${API}/tasks/${reopenTaskTarget.id}/reabrir`, {}, { withCredentials: true });
                toast.success("Tarefa reaberta!");
                fetchTasks();
                if (onSaved) onSaved();
              } catch { toast.error("Erro ao reabrir tarefa"); }
              setReopenTaskTarget(null);
            }}>Reabrir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Entity */}
      <EmpresaFormDialog open={editEmpresaOpen} onClose={() => setEditEmpresaOpen(false)}
        editItem={empresaRecord} onSaved={() => { setEditEmpresaOpen(false); if (onSaved) onSaved(); }} />
      <ContatoFormDialog open={!!editContatoItem} onClose={() => setEditContatoItem(null)}
        editItem={editContatoItem} onSaved={() => { setEditContatoItem(null); if (onSaved) onSaved(); }} />

      {/* Note Editor */}
      <NoteEditorDialog open={noteEditorOpen}
        onClose={() => { setNoteEditorOpen(false); setEditingNote(null); }}
        editingNote={editingNote} negocioId={negocio?.id} tc500={tc500}
        onSaved={() => { fetchNotes(); setNoteEditorOpen(false); setEditingNote(null); if (onSaved) onSaved(); }} />

      {/* Note Delete */}
      <AlertDialog open={!!noteDeleteTarget} onOpenChange={() => setNoteDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("notes.confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("notes.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={async () => {
              if (!noteDeleteTarget) return;
              try {
                await axios.delete(`${API}/notes/${noteDeleteTarget.id}`, { withCredentials: true });
                toast.success(t("notes.deleted"));
                fetchNotes();
                if (onSaved) onSaved();
              } catch { toast.error("Erro ao excluir"); }
              setNoteDeleteTarget(null);
            }}>{t("notes.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Detail */}
      <Dialog open={!!historyDetailEntry} onOpenChange={() => setHistoryDetailEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5" style={{ color: tc500 }} />Detalhes da alteração
            </DialogTitle>
          </DialogHeader>
          {historyDetailEntry && (() => {
            const before = historyDetailEntry.data_before || {};
            const after = historyDetailEntry.data_after || {};
            const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
              k => !["_id", "id", "tenant_id", "deleted", "updated_at"].includes(k)
            );
            const changed = allKeys.filter(k => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
            const formatVal = (v) => {
              if (v === null || v === undefined || v === "") return "—";
              if (typeof v === "object") return JSON.stringify(v);
              return String(v);
            };
            const formatLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
            return (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">{historyDetailEntry.description}</p>
                <p className="text-[11px] text-slate-400">{fmtDate(historyDetailEntry.created_at)}{historyDetailEntry.user_name ? ` · ${historyDetailEntry.user_name}` : ""}</p>
                {changed.length === 0 ? (
                  <p className="text-sm text-slate-400 italic py-2">Sem diferenças detalhadas disponíveis.</p>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {changed.map(k => (
                      <div key={k} className="rounded-lg border border-slate-200 p-3 space-y-1.5">
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{formatLabel(k)}</p>
                        {before[k] !== undefined && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-semibold text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">Antes</span>
                            <span className="text-xs text-slate-600 break-all">{formatVal(before[k])}</span>
                          </div>
                        )}
                        {after[k] !== undefined && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-semibold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">Depois</span>
                            <span className="text-xs text-slate-700 break-all">{formatVal(after[k])}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Email Composer */}
      <EmailComposerModal open={emailComposerOpen} onClose={() => setEmailComposerOpen(false)}
        negocio={negocio} contatos={contatoRecord ? [contatoRecord] : []}
        empresas={empresaRecord ? [empresaRecord] : []} stages={stages} pipeline={pipeline} onSent={fetchEmails} />

      {/* Email Detail */}
      <Dialog open={!!emailDetailItem} onOpenChange={() => setEmailDetailItem(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: tc500 }} />{t("emails.viewBody")}
            </DialogTitle>
          </DialogHeader>
          {emailDetailItem && (
            <div className="space-y-3">
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">{t("emails.to")}</span>
                  <span className="font-medium text-slate-700">{emailDetailItem.para?.join(", ")}</span>
                </div>
                {emailDetailItem.cc?.length > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">{t("emails.cc")}</span>
                    <span className="font-medium text-slate-700">{emailDetailItem.cc.join(", ")}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">{t("emails.subject")}</span>
                  <span className="font-semibold text-slate-800">{emailDetailItem.assunto}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">{t("emails.sentBy")}</span>
                  <span className="font-medium text-slate-700">{emailDetailItem.created_by_name || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">{t("emails.sentAt")}</span>
                  <span className="font-medium text-slate-700">{fmtDate(emailDetailItem.enviado_em || emailDetailItem.created_at)}</span>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="prose prose-sm max-w-none text-sm text-slate-700"
                  dangerouslySetInnerHTML={{ __html: emailDetailItem.corpo_html }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Context Preview */}
      <Dialog open={aiContextOpen} onOpenChange={setAiContextOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5" style={{ color: tc500 }} />Dados enviados para a IA
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {aiContextLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} />
                <span className="text-sm">Carregando contexto...</span>
              </div>
            ) : (
              <pre data-testid="ai-context-preview"
                className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto"
              >{aiContextText}</pre>
            )}
          </div>
          <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[10px] text-slate-400">Este é o texto exato enviado como mensagem ao seu Assistente OpenAI.</p>
            <button data-testid="ai-context-copy-btn" type="button"
              onClick={() => { navigator.clipboard.writeText(aiContextText); toast.success("Copiado!"); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded px-2.5 py-1 text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors">
              <Copy className="w-3 h-3" />Copiar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick WhatsApp */}
      <Dialog open={quickWaOpen} onOpenChange={(v) => { setQuickWaOpen(v); if (!v) setQuickWaText(""); }}>
        <DialogContent className="max-w-lg !p-0 overflow-hidden border-0">
          <div className="flex items-center gap-2.5 px-5 py-3.5 bg-emerald-500">
            <MessageCircle className="w-4.5 h-4.5 text-white" />
            <span className="text-sm font-semibold text-white">WhatsApp Rápido</span>
          </div>
          <div className="px-5 pb-5 pt-3 space-y-3">
            <textarea data-testid="quick-wa-text" value={quickWaText} onChange={e => setQuickWaText(e.target.value)}
              placeholder="Digite sua mensagem..." rows={5}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-emerald-400 resize-none"
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleQuickWaSend(); } }} />
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">Enviando para <span className="font-medium text-slate-600">{contatos?.find(c => c.id === negocio?.contato_id)?.nome || negocio?.contato_nome || "—"}</span></p>
              <button data-testid="quick-wa-send-btn" type="button" onClick={handleQuickWaSend}
                disabled={!quickWaText.trim() || quickWaSending}
                className="inline-flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 transition-colors">
                {quickWaSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Enviar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Perder */}
      <Dialog open={perderOpen} onOpenChange={setPerderOpen}>
        <DialogContent className="max-w-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">{t("negocios.loseTitle")}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t("negocios.selectLossReason")}</p>
            </div>
          </div>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {motivos.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <p className="text-sm">Nenhum motivo cadastrado.</p>
                <p className="text-xs mt-1">Cadastre em Configurações &rarr; Pipeline &rarr; Motivos de Perda</p>
              </div>
            ) : motivos.map(m => (
              <button key={m.id} type="button" onClick={() => setSelectedMotivo(m.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 text-left transition-colors ${selectedMotivo === m.id ? "bg-transparent" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                style={selectedMotivo === m.id ? { borderColor: "#ef4444" } : undefined}>
                <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                  style={selectedMotivo === m.id ? { borderColor: "#ef4444", backgroundColor: "#ef4444" } : { borderColor: "#cbd5e1" }}>
                  {selectedMotivo === m.id && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <span className="text-sm font-medium">{m.nome}</span>
              </button>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setPerderOpen(false)}>
              <X className="w-4 h-4 mr-2" />{t("common.cancel")}
            </Button>
            <Button className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
              onClick={handlePerder} disabled={perdendo || (motivos.length > 0 && !selectedMotivo)}>
              {perdendo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {t("negocios.confirmLoss")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visualizar Todos os Dados */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-slate-500" />Todos os Dados — {negocio?.titulo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 mt-2">
            {negocio && Object.entries(negocio).map(([key, val]) => {
              if (key === "custom_fields" && val && typeof val === "object") {
                return (
                  <div key={key}>
                    <div className="flex gap-3 py-1.5 border-b border-slate-100">
                      <span className="w-40 text-xs font-semibold text-slate-400 flex-shrink-0">{key}</span>
                      <span className="text-xs text-slate-500 italic">&darr; campos abaixo</span>
                    </div>
                    {Object.entries(val).map(([cf_key, cf_val]) => (
                      <div key={cf_key} className="flex gap-3 py-1.5 border-b border-slate-50 pl-4">
                        <span className="w-36 text-xs text-slate-400 flex-shrink-0">  {cf_key}</span>
                        <span className="text-xs text-slate-700 break-all">{String(cf_val ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div key={key} className="flex gap-3 py-1.5 border-b border-slate-100">
                  <span className="w-40 text-xs font-semibold text-slate-400 flex-shrink-0">{key}</span>
                  <span className="text-xs text-slate-700 break-all">{String(val ?? "—")}</span>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
